import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/apiClient';
import JobCostReport from './JobCostReport';
import ManHoursReport from './ManHoursReport';

/**
 * The project workspace's Reports tab (issue #347).
 *
 * The tab has existed in the nav rail since the CMS expansion but fell through
 * to ComingSoon. This gives it its first real report and, more importantly, the
 * shape the rest will slot into.
 *
 * ⚠️ ONE PERMISSION PER REPORT, not one for the tab. #347: *"Reports tab is
 * its own permission right now but I want to permission each individual report so
 * some users can see reports others cannot."* So:
 *
 *   • `reports.view`      → may open this tab at all (handled by the workspace)
 *   • `reports.<report>`  → may see THAT report's figures
 *
 * Portfolio WIP already worked this way (`financials.view_portfolio`); REPORTS
 * below generalises it. Adding a report means adding an entry here plus its
 * permission — never widening an existing one.
 *
 * ⚠️ HIDING A CARD IS PRESENTATION, NOT ENFORCEMENT. Every report's endpoint
 * carries the same permission server-side, plus a per-job visibility check, so a
 * user who reaches the URL another way still gets nothing. Same rule as
 * `lib/access.ts`.
 */

interface ReportEntry {
  key: string;
  name: string;
  blurb: string;
  /** Server-side gate for this report; the card is hidden without it. */
  permission: string;
  /**
   * Asks the Full screen overlay for slimmer side margins (issue #602). Per
   * report, because the overlay is shared: a report with a wide table opts in,
   * and the rest keep the standard margin.
   */
  fullWidth?: boolean;
  /** `mode.fullscreen` is whether the overlay is up, for a report that adapts. */
  render: (projectId: number, mode: { fullscreen: boolean }) => React.ReactNode;
}

const REPORTS: ReportEntry[] = [
  {
    key: 'job-cost',
    name: 'Job Cost Management Report',
    blurb: 'Estimated against actual cost for every cost code on this job, split by '
      + 'labor, material, equipment, subs and other, with change orders totalled separately.',
    permission: 'reports.job_cost',
    fullWidth: true,
    render: (projectId, { fullscreen }) => <JobCostReport projectId={projectId} compact={fullscreen} />,
  },
  {
    key: 'man-hours',
    name: 'Man Hours Report',
    blurb: 'Estimated man hours (labor dollars ÷ the company rate) against actual Paycor '
      + 'hours for every cost code, with change orders totalled separately.',
    permission: 'reports.man_hours',
    render: (projectId) => <ManHoursReport projectId={projectId} />,
  },
];

const css = `
  .prt-list { display:grid; grid-template-columns:repeat(auto-fill,minmax(320px,1fr)); gap:var(--ew-s-3); }
  .prt-card { text-align:left; background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md);
    padding:var(--ew-s-4) var(--ew-s-5); cursor:pointer; transition:border-color var(--ew-dur-fast) var(--ew-ease); }
  .prt-card:hover { border-color:var(--ew-green); }
  .prt-card:focus-visible { outline:2px solid var(--ew-green); outline-offset:1px; }
  .prt-card h3 { font:var(--ew-fw-semibold) 14.5px/1.3 var(--ew-font); color:var(--ew-dark); margin:0; }
  .prt-card p { margin:6px 0 0; font:var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); color:var(--ew-n-500); }
  .prt-back { display:inline-flex; align-items:center; gap:6px; background:none; border:0; cursor:pointer; padding:0;
    margin-bottom:var(--ew-s-4); font:var(--ew-fw-semibold) 12.5px/1 var(--ew-font); color:var(--ew-n-600); }
  .prt-back:hover { color:var(--ew-dark); }
  .prt-intro { margin:0 0 var(--ew-s-4); font:var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font); color:var(--ew-n-500); }
  .prt-none { background:#fff; border:1px solid var(--ew-n-200); border-radius:var(--ew-r-md);
    padding:var(--ew-s-8) var(--ew-s-6); text-align:center; }
  .prt-none h3 { font:var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color:var(--ew-dark); margin:0; }
  .prt-none p { margin:var(--ew-s-2) auto 0; max-width:44ch;
    font:var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font); color:var(--ew-n-500); }
  .prt-toolbar { display:flex; align-items:center; justify-content:space-between; gap:var(--ew-s-3); margin-bottom:var(--ew-s-4); }
  .prt-expand { display:inline-flex; align-items:center; gap:6px; cursor:pointer;
    background:#fff; border:1px solid var(--ew-n-300); border-radius:var(--ew-r-md); padding:7px 12px;
    font:var(--ew-fw-semibold) 12px/1 var(--ew-font); color:var(--ew-n-600); }
  .prt-expand:hover { border-color:var(--ew-green); color:var(--ew-dark); }
  /* Full screen is an OVERLAY, not the browser Fullscreen API — the API is
     unsupported on arbitrary elements in iPhone Safari and repaints with a
     black backdrop elsewhere, while a fixed overlay behaves identically
     everywhere and keeps the same React subtree mounted (no state loss). */
  .prt-full { position:fixed; inset:0; z-index:1000; background:var(--ew-n-50, #F7F7F6);
    overflow:auto; padding:var(--ew-s-4) var(--ew-s-5) var(--ew-s-6); }
  /* Slimmer side margins for a report that asks for them (ReportEntry.fullWidth).
     A modifier rather than a change to .prt-full, which every report shares. */
  .prt-full-wide { padding-left:var(--ew-s-3); padding-right:var(--ew-s-3); }
  .prt-full-bar { display:flex; align-items:center; justify-content:space-between; gap:var(--ew-s-3);
    margin-bottom:var(--ew-s-4); }
  .prt-full-bar h2 { font:var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color:var(--ew-dark); margin:0; }
  .prt-full-hint { font:var(--ew-fw-regular) 12px/1 var(--ew-font); color:var(--ew-n-400); margin-right:var(--ew-s-3); }
  /* Let the report tables use the viewport they just gained. These override the
     70vh cap each report sets in its own module-local <style> block — an
     override by a MORE SPECIFIC selector, not a reuse of the class (see the
     module-local CSS rule): it applies only inside the overlay. */
  .prt-full .jcr-wrap, .prt-full .mhr-wrap { max-height: calc(100vh - 130px); }
`;

export default function ProjectReportsTab({ projectId }: { projectId: number }) {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60_000 });
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const expandBtnRef = useRef<HTMLButtonElement | null>(null);

  // Esc exits, and the page behind the overlay must not scroll — the overlay
  // owns the scrollbar while it is up. Both undone the moment it closes.
  //
  // FOCUS IS MANAGED, not left to fall where the unmounted trigger dropped it
  // (review, #356): entering moves focus to the exit control, Tab is trapped
  // inside the overlay (the page behind is visually hidden but still in the DOM
  // — aria-modal on the wrapper excludes it for AT, this trap excludes it for
  // the keyboard), and exiting hands focus back to the ⛶ trigger.
  useEffect(() => {
    if (!fullscreen) return;
    const overlay = overlayRef.current;
    overlay?.querySelector<HTMLElement>('[data-prt-exit]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFullscreen(false); return; }
      if (e.key !== 'Tab' || !overlay) return;
      const focusables = [...overlay.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )].filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      // Also catches focus that escaped (or started) OUTSIDE the overlay —
      // either Tab direction pulls it back in rather than walking the page.
      if (e.shiftKey && (active === first || !overlay.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && (active === last || !overlay.contains(active))) {
        e.preventDefault(); first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      expandBtnRef.current?.focus();
    };
  }, [fullscreen]);

  const perms = me?.permissions ?? [];
  const isAdmin = me?.roles?.includes('Admin') ?? false;
  const available = REPORTS.filter((r) => isAdmin || perms.includes(r.permission));

  /**
   * Open a single report straight away when it is the only one available.
   *
   * With one report, a list of one card is a pointless extra click. This stops
   * being the behaviour the moment a second report is granted, which is the
   * right time for a list to appear.
   */
  const open = available.find((r) => r.key === openKey) ?? (available.length === 1 ? available[0] : null);
  const showBack = open != null && available.length > 1;

  // If the open report disappears out from under the overlay — a ['me'] refresh
  // after the per-report grant was revoked — the dialog unrenders but the
  // fullscreen effect above keys only on `fullscreen`, so its cleanup would
  // never run: body scroll stays locked and the key handler stays installed
  // under the report list (review, #356). Reset the state instead, which runs
  // that cleanup properly.
  useEffect(() => {
    if (fullscreen && !open) setFullscreen(false);
  }, [fullscreen, open]);

  // Wait for `me` before deciding, so the tab doesn't flash "no reports" while
  // the permission list is still loading.
  if (!me) {
    return <p className="prt-intro" style={{ color: 'var(--ew-n-500)' }}>Loading…</p>;
  }

  // Not "coming soon" — the reports exist, this user just holds none of them.
  // Saying so plainly is what stops someone chasing a bug that isn't one.
  if (!available.length) {
    return (
      <>
        <style>{css}</style>
        <div className="prt-none">
          <h3>No reports shared with your role</h3>
          <p>
            Reports are granted one at a time, so some people see reports others cannot.
            Ask an admin for the ones you need.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      {open ? (
        // ⚠️ ONE subtree for both modes. Only the wrapper's class changes, so
        // expanding never remounts the report — query cache, scroll position
        // and the zero-rows toggle all survive the flip.
        <div
          ref={overlayRef}
          className={fullscreen ? (open.fullWidth ? 'prt-full prt-full-wide' : 'prt-full') : undefined}
          role={fullscreen ? 'dialog' : undefined}
          aria-modal={fullscreen ? true : undefined}
          aria-label={fullscreen ? open.name : undefined}
        >
          {fullscreen ? (
            <div className="prt-full-bar">
              <h2>{open.name}</h2>
              <span>
                <span className="prt-full-hint">Esc exits</span>
                <button className="prt-expand" data-prt-exit onClick={() => setFullscreen(false)}>✕ Exit full screen</button>
              </span>
            </div>
          ) : (
            <div className="prt-toolbar">
              {showBack ? (
                <button className="prt-back" style={{ marginBottom: 0 }} onClick={() => { setFullscreen(false); setOpenKey(null); }}>
                  ← All reports
                </button>
              ) : <span />}
              <button ref={expandBtnRef} className="prt-expand" onClick={() => setFullscreen(true)} title="View this report full screen">
                ⛶ Full screen
              </button>
            </div>
          )}
          {open.render(projectId, { fullscreen })}
        </div>
      ) : (
        <div>
          <p className="prt-intro">Reports available to you for this job.</p>
          <div className="prt-list">
            {available.map((r) => (
              <button key={r.key} className="prt-card" onClick={() => setOpenKey(r.key)}>
                <h3>{r.name}</h3>
                <p>{r.blurb}</p>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
